import { DateYmd } from 'hydra-data-types';
import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import { convertToISODate, getDateFormat } from 'ts-utils';
import { Color } from '../../../config/Color';
import { appOverride } from '../../../util/AppUtils';
import { useAppStore } from '../../../store/Store';
import { CrossColorable, TickColorable } from '../../assets/icons';
import { QuestionWrapper } from '../../styles/Containers';
import { CopyContainer } from '../../styles/CopyContainer';
import { DobNumberInput } from '../../styles/DobNumberInput';
import { ErrorText } from '../../styles/ErrorMessage';
import { InfoBox } from '../../styles/InfoBox';
import { BodySM } from '../../styles/Typography';
import { getDobError, processDobOnKeyDown } from './DateOfBirthUtils';
import { LayoutActions } from './LayoutActions';

export type DateOfBirthLayoutConfig = {
  // there is no collectsPIDData prop, as date of birth is always PII
  minimumAge?: number; // error condition if implied age is less than this
  /** Overrides the default "What is your date of birth?" prompt. */
  question?: string;
  primaryButtonTitle: string;
  infoBox?: {
    titleText?: string;
    bodyText: string;
  };
  onPressPrimaryButton?: (dateOfBirth: DateYmd) => void;
};

type DateOfBirthLayoutProps = DateOfBirthLayoutConfig & {
  onPressPrimaryButton: (dateOfBirth: DateYmd) => void;
};

/**
 *
 * @param minimumAge - will display error if user is younger than this
 * @returns
 */
export const DateOfBirthLayout: FunctionComponent<DateOfBirthLayoutProps> = (props) => {
  const { minimumAge = 13 } = props;
  const [error, setError] = useState<string>('');
  const [dobUserInputCurrent, setDobUserInputCurrent] = useState<string>('');
  const country = useAppStore((state) => state.geolocation.country);
  const dateFormat = getDateFormat(country);
  const color = error === '' ? Color.content.default : Color.state.error;

  const isEmpty = dobUserInputCurrent.trim().length === 0;

  const getBorderColor = () => {
    if (error !== '') return Color.state.error;
    return appOverride({
      nerva: isEmpty ? 'rgba(245, 245, 245, 0.25)' : '#FFFFFF',
      default: Color.decorative.default,
    });
  };

  const borderColor = getBorderColor();

  useEffect(
    function setErrorStateOnInputValueChange() {
      setError(getDobError({ dobUserInputCurrent, minimumAge, dateFormat }));
    },
    [dobUserInputCurrent, dateFormat]
  );

  const InputIcon = () => {
    let icon: ReactElement | null = null;
    if (dobUserInputCurrent.length === 10) {
      if (error === '') {
        icon = <TickColorable test-id={'dob-tick'} color={Color.state.success} />;
      } else {
        icon = <CrossColorable test-id={'dob-cross'} color={Color.state.error} />;
      }
    }
    return <React.Fragment>{icon}</React.Fragment>;
  };

  function handleOnKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    event.preventDefault();
    setDobUserInputCurrent(
      processDobOnKeyDown({
        dob: dobUserInputCurrent,
        key: event.key,
        dateFormat,
      })
    );
  }

  function nextButtonPressHandler() {
    const segments = dobUserInputCurrent.split('/');
    if (segments.length !== 3) {
      setError(`Please enter a date in the format ${dateFormat.toLowerCase()}`);
    } else {
      const isoDate = convertToISODate(dobUserInputCurrent, dateFormat);
      props.onPressPrimaryButton(isoDate as DateYmd);
    }
  }

  return (
    <QuestionWrapper style={{ margin: 'auto' }}>
      <CopyContainer style={{ margin: '2em auto' }}>
        <BodySM data-test="dobQuestion">{props.question ?? 'What is your date of birth?'}</BodySM>
      </CopyContainer>
      <DobUserInputContainer
        borderColor={borderColor}
        color={color}
        fontSize={24}
        isEmpty={isEmpty}
      >
        <DobNumberInput
          width={200}
          marginLeft={24}
          data-test="dob-user-input"
          value={dobUserInputCurrent}
          onKeyDown={handleOnKeyDown}
          onChange={(event) => event.preventDefault()}
          placeholder={dateFormat.toLowerCase()}
          aria-label={`date of birth in ${dateFormat.toLowerCase()} format`}
          inputMode="numeric"
        />
        <IconContainer>
          <InputIcon />
        </IconContainer>
      </DobUserInputContainer>
      <ErrorText>{error === '' ? '\u00A0' : error}</ErrorText>
      {props.infoBox && <InfoBox {...props.infoBox} />}
      <LayoutActions
        primaryButtonTitle={props.primaryButtonTitle}
        onPressPrimaryButton={nextButtonPressHandler}
        primaryButtonDisabled={dobUserInputCurrent.length !== 10 || error !== ''}
      />
    </QuestionWrapper>
  );
};

const DobUserInputContainer = styled.div<{
  borderColor: string;
  color?: string;
  fontSize?: number;
  isEmpty: boolean;
}>`
  display: flex;
  flex-direction: row;
  border-radius: ${appOverride({ nerva: '14px', default: '8px' })};
  background-color: ${appOverride({ nerva: 'transparent', default: Color.surface.muted })};
  border: 2px solid ${({ borderColor }) => borderColor} !important;
  width: ${appOverride({ nerva: '100%', default: '300px' })};
  margin: ${appOverride({ nerva: '0', default: '0px 16px' })};
  box-sizing: border-box;
  padding: ${appOverride({ nerva: '0 16px', default: '0' })};

  > * {
    font-size: ${({ fontSize }) => fontSize ?? 18}px;
    color: ${({ color }) =>
      appOverride({ nerva: Color.content.default, default: color ?? Color.decorative.default })};
    &:focus {
      outline: none;
    }
    &::placeholder {
      color: ${appOverride({ nerva: '#f5f5f5', default: 'inherit' })};
    }
  }
`;

const IconContainer = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: center;
  align-items: center;
  flex-direction: row;
`;
